import { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Button,
  Checkbox,
  Paper,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useDispatch, useSelector } from "react-redux";

import { prepareBootcampOptions, prepareFormOptions, ROLE_TYPES } from "../../utils/utils";
import AutoCompleteTagInput from "../../components/AutoCompleteTagInput/AutoCompleteTagInput";
import NoDataAvailable from "../../components/NoDataAvailable/NoDataAvailable";
import Loader from "../../components/Loader/Loader";

import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";



import {
  fetchDashboardData,
  clearDashboardData,
  fetchBootCampAndFormList,
  fetchDashboardConfig,
} from "../../store/dashboard";

import "./bootcampAssessmentDashboard.scss";
import DynamicTable from "../../components/Table/DynamicTable";
import { HelpTexts } from "../../constants/constants";

dayjs.extend(utc);
dayjs.extend(tz);

/* -------------------- CONSTANTS -------------------- */

const BOOTCAMP_ASSESSMENT_DASHBOARD_TABS = {
  CLASSROOM_PHASE: "CLASSROOM_PHASE",
  SHADOW_PROJECT_PHASE: "SHADOW_PROJECT_PHASE",
  OVERALL: "OVERALL",
};

const FORM_TYPES = {
  CLASSROOM_PERFORMANCE: "CLASSROOM_ASSESSMENT_PERFORMANCE",
  CLASSROOM_BEHAVIOUR: "CLASSROOM_BEHAVIOUR",
  SHADOW_PERFORMANCE: "SHADOW_PROJECT_ASSESSMENT_PERFORMANCE",
  SHADOW_BEHAVIOUR: "SHADOW_PROJECT_BEHAVIOUR",
};

// Map which individual cards appear in which tab
const TAB_MAPPINGS = {
  [BOOTCAMP_ASSESSMENT_DASHBOARD_TABS.CLASSROOM_PHASE]: [
    FORM_TYPES.CLASSROOM_PERFORMANCE,
    FORM_TYPES.CLASSROOM_BEHAVIOUR
  ],
  [BOOTCAMP_ASSESSMENT_DASHBOARD_TABS.SHADOW_PROJECT_PHASE]: [
    FORM_TYPES.SHADOW_PERFORMANCE,
    FORM_TYPES.SHADOW_BEHAVIOUR,
  ],
  [BOOTCAMP_ASSESSMENT_DASHBOARD_TABS.OVERALL]: [
    FORM_TYPES.CLASSROOM_PERFORMANCE,
    FORM_TYPES.CLASSROOM_BEHAVIOUR,
    FORM_TYPES.SHADOW_PERFORMANCE,
    FORM_TYPES.SHADOW_BEHAVIOUR,
  ],
};

const FORM_DISPLAY_NAMES = {
  [FORM_TYPES.CLASSROOM_PERFORMANCE]: "Technical Assessment",
  [FORM_TYPES.CLASSROOM_BEHAVIOUR]: "Behavioural Assessment (Phase I)",
  [FORM_TYPES.SHADOW_PERFORMANCE]: "Project Assessment",
  [FORM_TYPES.SHADOW_BEHAVIOUR]: "Behavioural Assessment (Phase II)",
};

const ZONES = [
  { label: "Red zone", range: "0–2.5", color: "#d32f2f" },
  { label: "Amber zone", range: "2.6–3.7", color: "#f9a825" },
  { label: "Green zone", range: "3.8–5", color: "#388e3c" },
];
const parseConfig = (configStr) => {
  if (!configStr) return null;
  const ranges = configStr.split(',').map(range =>
    range.split('-').map(val => parseFloat(val.trim()))
  );

  if (ranges.length < 3) return null;

  return {
    red: { min: ranges[0][0], max: ranges[0][1], color: "#d32f2f" },
    amber: { min: ranges[1][0], max: ranges[1][1], color: "#f9a825" },
    green: { min: ranges[2][0], max: ranges[2][1], color: "#388e3c" }
  };
};

const getZone = (score) => {
  const s = Number(score);
  if (s >= 3.8) return { label: "Green", color: "#388e3c" };
  if (s >= 2.6) return { label: "Amber", color: "#f9a825" };
  return { label: "Red", color: "#d32f2f" };
};
const { NON_ACTIVE_ATTENDEE } = HelpTexts;
const ZONE_COLORS = {
  GREEN: "#388e3c",
  AMBER: "#f9a825",
  RED: "#d32f2f",
  PURPLE: "#b32682",
  BORDER: "#e0e0e0",
};

const renderZoneChip = (zone) => (
  <Chip
    label={zone}
    size="small"
    sx={{
      fontWeight: "bold",
      bgcolor: ZONE_COLORS[zone],
      color: "#fff",
      width: "80px",
    }}
  />
);



const mapUserData = (item) => {
  const user = item.user || {};
  return {
    fullName: user.fullName || "N/A",
    newerEmail: user.email || "N/A",
    profilePictureUrl: user.profilePictureUrl || "",
    newerId: user.employeeCode || user.id,
    userDeleted: user.deleted || false,
    attendeeStatus: item.attendeeStatus,
    statusUpdatedDate: item.statusUpdatedDate,
    competency: user.competency?.name || "Quality Engineering",
  };
};
/* -------------------- COMPONENT -------------------- */

const BootcampAssessmentDashboard = ({
  onClose,
  preselectedBootcampId = null,
}) => {
  const dispatch = useDispatch();

  const { roles } = useSelector((state) => state.security);
  const { data: dashboardData, loading } = useSelector((state) => state.dashboard);
  const { filterList, optionsLoading, permissions, dashboardConfig } = useSelector((state) => state.dashboard);

  const isHr = ROLE_TYPES.isHr(roles);

  const [activeTab, setActiveTab] = useState(BOOTCAMP_ASSESSMENT_DASHBOARD_TABS.CLASSROOM_PHASE);
  const [filters, setFilters] = useState({
    bootcampIds: [],
    formIds: []
  }); const lastRequestRef = useRef("");

  const isSpoc = useMemo(() => permissions?.spoc === true, [permissions]);
  const isAttendee = useMemo(() => permissions?.attendee === true, [permissions]);
  const userRole = useMemo(() => {
    if (isSpoc) return { asSpoc: true };
    if (isHr) return { asHr: true };
    if (isAttendee) return { asAttendee: true }
    return {};
  }, [permissions?.spoc, isHr, permissions?.attendee]);

  const bootcampOptions = useMemo(() => prepareBootcampOptions(filterList), [filterList]);
  const formOptions = useMemo(() => prepareFormOptions(filterList, filters.bootcampIds.map((b) => b.id)), [filterList, filters.bootcampIds]);

  const overallTableColumns = useMemo(() => [
    {
      key: "technicalScore",
      label: "Technical Assessment(s) Score",
      minWidth: "14rem",
      render: (v) => renderScoreChip(v)
    },
    {
      key: "beh1Score",
      label: "Behavioural Assessment Score (Phase I)",
      minWidth: "18rem",
      render: (v) => renderScoreChip(v)
    },
    {
      key: "projectScore",
      label: "Project Assessment Score",
      minWidth: "12rem",
      render: (v) => renderScoreChip(v)
    },
    {
      key: "beh2Score",
      label: "Behavioural Assessment Score (Phase II)",
      minWidth: "18rem",
      render: (v) => renderScoreChip(v)
    },
  ], []);

  const dynamicZones = useMemo(() => {
    const parsed = parseConfig(dashboardConfig);
    if (!parsed) {
      return [
        { label: "Red zone", min: 0, max: 2.5, color: "#d32f2f", range: "0–2.5" },
        { label: "Amber zone", min: 2.6, max: 3.7, color: "#f9a825", range: "2.6–3.7" },
        { label: "Green zone", min: 3.8, max: 5, color: "#388e3c", range: "3.8–5" },
      ];
    }
    return [
      { label: "Red zone", ...parsed.red, range: `${parsed.red.min}-${parsed.red.max}` },
      { label: "Amber zone", ...parsed.amber, range: `${parsed.amber.min}-${parsed.amber.max}` },
      { label: "Green zone", ...parsed.green, range: `${parsed.green.min}-${parsed.green.max}` },
    ];
  }, [dashboardConfig]);

  const getDynamicZone = (score) => {
    const s = parseFloat(score);
    const greenZone = dynamicZones.find(z => z.label.toLowerCase().includes('green'));
    if (greenZone && s >= greenZone.min) {
      return greenZone;
    }

    const match = dynamicZones.find(z => s >= z.min && s <= z.max);
    return match || { label: "N/A", color: "#9e9e9e" };
  };
  const renderScoreChip = (v) => {
    const score = Number(v) || 0;
    const zone = getDynamicZone(score);
    return (
      <Chip
        label={score}
        size="small"
        sx={{ fontWeight: "bold", bgcolor: zone.color, color: "#fff", minWidth: "45px" }}
      />
    );
  };



  /* -------------------- EFFECTS -------------------- */

  useEffect(() => {
    dispatch(fetchDashboardConfig());
    dispatch(fetchBootCampAndFormList());
  }, [dispatch]);

  useEffect(() => {
    if (preselectedBootcampId && bootcampOptions.length) {
      const match = bootcampOptions.find((b) => b.id === preselectedBootcampId);
      if (match) {
        setFilters((prev) => ({
          ...prev,
          bootcampIds: [match],
          formIds: []
        }));
      }
    }
  }, [preselectedBootcampId, bootcampOptions]);

  useEffect(() => {
    if (filters.bootcampIds.length === 0) {
      dispatch(clearDashboardData());
      return;
    }
    const currentRequestSignature = JSON.stringify({
      bootcamps: filters.bootcampIds.map(b => b.id),
      forms: filters.formIds.map(f => f.id),
      role: userRole
    });
    if (lastRequestRef.current === currentRequestSignature) return;
    lastRequestRef.current = currentRequestSignature;

    dispatch(fetchDashboardData({
      bootcampIds: filters.bootcampIds.map((b) => b.id),
      formIds: filters.formIds.map((f) => f.id),
      paginated: false,
      ...userRole
    }));
  }, [filters, userRole, dispatch]);
  useEffect(() => {
    return () => {
      dispatch(clearDashboardData());
    };
  }, [dispatch]);

  /* -------------------- DATA PROCESSING -------------------- */

  const calculateSummary = (results, formKeys) => {
    if (!results?.length || !formKeys?.length) return null;

    let totalScoreSum = 0;
    let totalAssessmentCount = 0;

    results.forEach((item) => {
      formKeys.forEach((key) => {
        const score = item.scoresByFormType?.[key] || 0;
        const count = item.countsByFormType?.[key] || 0;
        totalScoreSum += score;
        totalAssessmentCount += count;
      });
    });
    const avgScore = totalAssessmentCount > 0
      ? (totalScoreSum / totalAssessmentCount).toFixed(2)
      : "0.00";

    return {
      count: totalAssessmentCount,
      score: avgScore,
      zone: getDynamicZone(avgScore).label,
    };
  };

  const dashboardSummaryList = useMemo(() => {
    const results = dashboardData?.results || [];
    if (!results.length) return [];

    const activeKeys = TAB_MAPPINGS[activeTab] || [];
    return activeKeys.map(key => ({
      key,
      title: FORM_DISPLAY_NAMES[key],
      data: calculateSummary(results, [key])
    }));
  }, [dashboardData, activeTab]);

  const processedOverallData = useMemo(() => {
    const results = dashboardData?.results || [];

    return results.map((item) => {
      const ts = item.scoresByFormType?.[FORM_TYPES.CLASSROOM_PERFORMANCE] ?? 0;
      const b1 = item.scoresByFormType?.[FORM_TYPES.CLASSROOM_BEHAVIOUR] ?? 0;
      const ps = item.scoresByFormType?.[FORM_TYPES.SHADOW_PERFORMANCE] ?? 0;
      const b2 = item.scoresByFormType?.[FORM_TYPES.SHADOW_BEHAVIOUR] ?? 0;

      return {
        ...mapUserData(item),
        technicalScore: ts,
        technicalZone: getDynamicZone(ts).label.toUpperCase(),
        beh1Score: b1,
        beh1Zone: getDynamicZone(b1).label.toUpperCase(),
        projectScore: ps,
        projectZone: getDynamicZone(ps).label.toUpperCase(),
        beh2Score: b2,
        beh2Zone: getDynamicZone(b2).label.toUpperCase(),
      };
    });
  }, [dashboardData]);

  /* -------------------- UI HELPERS -------------------- */

  const renderAssessmentSummary = (title, data) => (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 1 }}>
        <Typography variant="font_20_bold" sx={{ mb: 2 }}>{title}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} textAlign="center">
            <Typography variant="body2" sx={{ my: 1 }} >No. of Assessments</Typography>
            <Typography variant="h4">{data.count}</Typography>
          </Grid>
          <Grid item xs={12} md={6} textAlign="center">
            <Typography variant="body2" sx={{ my: 1 }}>Average Score</Typography>
            <Typography variant="h4">{renderScoreChip(data.score)}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderDashboardContent = () => {
    if (loading) return <Loader />;
    const activeData = dashboardSummaryList.filter(item => item.data && item.data.count > 0);

    if (activeData.length === 0) {
      return <NoDataAvailable message="No assessment data found for the selected criteria." />;
    }

    return (
      <Box sx={{ mt: 3 }}>

        <Box sx={{ maxHeight: "500px", overflowY: "auto" }}>
          {activeData.map((item) => (
            <Box key={item.key} sx={{ mb: 3 }}>
              {renderAssessmentSummary(item.title, item.data)}
            </Box>
          ))}
        </Box>

      </Box>
    );
  };;

  /* -------------------- RENDER -------------------- */

  return (
    <Grid container className="main-container">
      <Grid item xs={12} className="header-container">
        <Typography variant="font_21_bold">Performance Dashboard BOOT</Typography>
        <Box className="header-right-icons">
          <Button variant="outlined" color="secondary" onClick={onClose}>
            <Typography variant="outlineBtnLabel">Back</Typography>
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12} className="body-container" sx={{ p: 3, position: "relative", backgroundColor: "#f5f5f5" }}>
        <Box>
          <Paper elevation={0} sx={{ p: 2 }}  >
            <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              {/* Bootcamp Filter */}
              <Grid item xs={12} md={6}>
                <AutoCompleteTagInput
                  label="Select Bootcamps"
                  options={bootcampOptions}
                  selectedValue={filters.bootcampIds}
                  placeholder={filters.bootcampIds?.length ? null : "Select Bootcamp"}
                  disabled
                  loading={optionsLoading}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    const nextBootcamps = newValue.find((o) => o.all)
                      ? (filters.bootcampIds.length === bootcampOptions.length ? [] : bootcampOptions)
                      : newValue.filter(o => !o.all);

                    // Updating both simultaneously prevents the useEffect from seeing partial changes
                    setFilters({
                      bootcampIds: nextBootcamps,
                      formIds: []
                    });
                  }}
                  filterOptions={(options, params) => {
                    const filtered = createFilterOptions()(options, params);
                    return [{ name: "Select All", all: true }, ...filtered];
                  }}
                  renderOption={(props, option) => (
                    <li {...props} className="multi-tag-style">
                      <Checkbox size="small" checked={filters.bootcampIds.some(e => e.id === option.id) || (option.all && filters.bootcampIds.length === bootcampOptions.length)} color="secondary" />
                      <Typography variant="font_16">{option.name}</Typography>
                    </li>
                  )}
                  renderTags={(value, getTagProps) => value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
                  ))}
                />
              </Grid>

              {/* Legend */}
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={3} flexWrap="wrap" justifyContent="end">
                  {ZONES.map((z) => (
                    <Box key={z.label} display="flex" gap={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: z.color }} />
                      <Typography variant="body2">{z.label}: <strong>{z.range}</strong></Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>

            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              className="tab-head-container"
              textColor="secondary"
              indicatorColor="secondary"
            >
              <Tab label="Classroom Phase" value={BOOTCAMP_ASSESSMENT_DASHBOARD_TABS.CLASSROOM_PHASE} />
              <Tab label="Shadow Project Phase" value={BOOTCAMP_ASSESSMENT_DASHBOARD_TABS.SHADOW_PROJECT_PHASE} />
              <Tab label="Overall" value={BOOTCAMP_ASSESSMENT_DASHBOARD_TABS.OVERALL} />
            </Tabs>

          </Paper>
          {renderDashboardContent()}
          <Box sx={{ mt: 6 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Detailed Score Matrix</Typography>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Box sx={{ maxHeight: "800px", overflowY: "auto" }}>
                <DynamicTable
                  columns={overallTableColumns}
                  rows={processedOverallData}
                  showPagination={false}
                />
              </Box>
            </Paper>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default BootcampAssessmentDashboard;